// Today's order cut-off for the caller's warehouse (Settings › Dispatch ›
// Channel cut-offs, 2026-10-13) — GET /dispatch-admin/channel-rules/today.
// The Pick Queue and Shelf Drop screens gate their "must ship today"
// toggle on it: before the cut-off everything in the queue is still
// today's work. `cutoff` is "HH:MM" London, null = no cut-off today (a
// weekend row: the toggle never shows), undefined = not loaded yet
// (falls back to the old 13:00 so a slow network changes nothing).
// Re-read every REFRESH_MS and when the London date rolls over, so a
// screen left open overnight picks up the next day's row.
//
// Settings › Picking › "Must ship today" always on (Alex, 2026-11-04):
// on the weekdays ticked there the toggle shows all day, so the same
// response carries `due_filter_always` and `afterCutoff` is true from
// midnight on those days — every other day keeps the clock rule.
import { useEffect, useState } from "react";
import { fetchTodayCutoffs } from "../../api/dispatchChannelRules";
import { isAfterDispatchCutoff, londonDateOf } from "./dispatchDue";

const REFRESH_MS = 15 * 60_000;
const CHECK_MS = 60_000;

export interface OrderCutoff {
    /** Today's standard order cut-off, "HH:MM"; null = none today; undefined = unknown. */
    cutoff: string | null | undefined;
    /** The warehouse ship cut-off, "HH:MM" (undefined until loaded). */
    shipCutoff: string | undefined;
    /** Today is a Settings › Picking "always on" weekday: the toggle shows all day. */
    dueAlways: boolean;
    /**
     * The toggle should show and apply: past today's cut-off on the London
     * clock (re-evaluated every minute), or an always-on weekday.
     */
    afterCutoff: boolean;
}

export const useOrderCutoff = (): OrderCutoff => {
    const [cutoff, setCutoff] = useState<string | null | undefined>(undefined);
    const [shipCutoff, setShipCutoff] = useState<string | undefined>(undefined);
    const [dueAlways, setDueAlways] = useState<boolean>(false);
    const [afterCutoff, setAfterCutoff] = useState<boolean>(() => isAfterDispatchCutoff(Date.now()));

    useEffect(() => {
        let cancelled = false;
        let loadedFor = "";
        const load = async () => {
            try {
                const c = await fetchTodayCutoffs();
                if (cancelled) return;
                loadedFor = c.today;
                setCutoff(c.order_cutoff);
                setShipCutoff(c.ship_cutoff);
                setDueAlways(c.due_filter_always === true);
            } catch {
                // keep whatever we had — the 13:00 fallback until the first load
            }
        };
        load();
        const refresh = window.setInterval(load, REFRESH_MS);
        const tick = window.setInterval(() => {
            if (loadedFor && londonDateOf(Date.now()) !== loadedFor) load();
        }, CHECK_MS);
        return () => { cancelled = true; window.clearInterval(refresh); window.clearInterval(tick); };
    }, []);

    useEffect(() => {
        const update = () => setAfterCutoff(dueAlways || isAfterDispatchCutoff(Date.now(), cutoff));
        update();
        const id = window.setInterval(update, CHECK_MS);
        return () => window.clearInterval(id);
    }, [cutoff, dueAlways]);

    return { cutoff, shipCutoff, dueAlways, afterCutoff };
};
